import { useState, useEffect } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { FormField } from "./FormField";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Lock, Unlock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFormContext } from "react-hook-form";
import { toast } from "@/components/ui/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { parseUnits } from "viem";
import { HelpCircle } from "lucide-react";
import { useWalletState } from "@/hooks/useWalletState";

const NO_LIMIT_VALUE = "10000"; // 100% in basis points
const NO_LIMIT_DISPLAY = "0";   // What we show in the UI for "No Limit"

export function SecurityFeatures() {
  const { watch, setValue } = useFormContext();
  const { isTestnet } = useWalletState();
  
  // All security features off by default
  const [maxWalletType, setMaxWalletType] = useState(NO_LIMIT_DISPLAY); // No limit
  const [maxBuyType, setMaxBuyType] = useState(NO_LIMIT_DISPLAY); // No limit
  const [maxSellType, setMaxSellType] = useState(NO_LIMIT_DISPLAY); // No limit
  const [tradingDelayType, setTradingDelayType] = useState("0"); // No delay
  
  // Custom value states
  const [customMaxWallet, setCustomMaxWallet] = useState("");
  const [customMaxBuy, setCustomMaxBuy] = useState("");
  const [customMaxSell, setCustomMaxSell] = useState("");

  // Anti-Dump Settings - off by default
  const [maxSellAmount, setMaxSellAmount] = useState("0"); // No limit

  const [maxWalletLocked, setMaxWalletLocked] = useState(false);
  const [maxBuyLocked, setMaxBuyLocked] = useState(false);
  const [maxSellLocked, setMaxSellLocked] = useState(false);
  const [tradingDelayLocked, setTradingDelayLocked] = useState(false);
  const [tradingEnabledLocked, setTradingEnabledLocked] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const renderLockToggle = (locked: boolean, setLocked: (value: boolean) => void) => (
    isTestnet ? (
      <div className="flex items-center gap-2 ml-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 cursor-help">
                <span className="text-sm text-muted-foreground">Lock Settings</span>
                <Unlock className={`w-4 h-4 ${!locked ? 'text-muted-foreground' : 'hidden'}`} />
                <Lock className={`w-4 h-4 ${locked ? 'text-muted-foreground' : 'hidden'}`} />
                <Switch
                  checked={locked}
                  onCheckedChange={setLocked}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>If locked, this setting cannot be changed after token deployment</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ) : null
  );

  useEffect(() => {
    // Initialize security features with default values
    setValue("contractLocked", false);
    setValue("maxWalletPercentage", NO_LIMIT_VALUE); // No limit = 100%
    setValue("maxWalletAmount", watch("totalSupply") || "0"); // Full supply for no limit
    setValue("tradingDelay", "0"); // No delay
    setValue("antiDump", {
      enabled: false,
      maxBuyPercentage: NO_LIMIT_VALUE, // No limit = 100%
      maxSellPercentage: NO_LIMIT_VALUE  // No limit = 100%
    });
    setValue("tradingEnabled", true); // Enable trading on deploy
    setValue("permanentTrading", false); // Permanent trading disabled
    setValue("tradingEnabledLocked", false);
  }, []);

  const calculateTokenAmount = (percentage: number): string => {
    const totalSupply = watch("totalSupply");
    if (!totalSupply) return "0";
    
    try {
      // Convert percentage to decimal (e.g., 5% -> 0.05)
      const decimalPercentage = percentage / 100;
      
      // Calculate the actual token amount
      const tokenAmount = (parseFloat(totalSupply) * decimalPercentage).toString();
      
      // Return the raw amount - TokenForm will handle the decimals
      return tokenAmount;
    } catch (error) {
      console.error("Error calculating token amount:", error);
      return "0";
    }
  };

  const handleMaxWalletChange = (value: string) => {
    setMaxWalletType(value);
    
    if (value === NO_LIMIT_DISPLAY) {
      // No Limit = 100% in contract
      setValue("maxWalletPercentage", NO_LIMIT_VALUE);
      setValue("maxWalletAmount", watch("totalSupply") || "0");
    } else if (value === "custom") {
      setValue("maxWalletPercentage", "0");
      setValue("maxWalletAmount", "0");
    } else {
      // Store as regular percentage, TokenForm will convert to basis points
      setValue("maxWalletPercentage", value);
      
      // Convert percentage to actual token amount for display
      const tokenAmount = calculateTokenAmount(parseInt(value));
      setValue("maxWalletAmount", tokenAmount);
      
      // If max buy/sell amounts are higher than the new max wallet amount, adjust them down
      const maxBuyAmount = watch("maxBuyAmount");
      const maxSellAmount = watch("maxSellAmount");
      
      if (maxBuyAmount !== "0" && parseFloat(maxBuyAmount) > parseFloat(tokenAmount)) {
        setValue("maxBuyAmount", tokenAmount);
        setValue("antiDump.maxBuyPercentage", value);
        toast({
          title: "Max Buy Amount Adjusted",
          description: "Max buy amount has been adjusted to match the max wallet limit.",
        });
      }
      
      if (maxSellAmount !== "0" && parseFloat(maxSellAmount) > parseFloat(tokenAmount)) {
        setValue("maxSellAmount", tokenAmount);
        setValue("antiDump.maxSellPercentage", value);
        toast({
          title: "Max Sell Amount Adjusted",
          description: "Max sell amount has been adjusted to match the max wallet limit.",
        });
      }
    }
  };

  const handleCustomMaxWalletChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomMaxWallet(value);
    
    if (value && !isNaN(parseFloat(value))) {
      const percentage = parseFloat(value);
      // Ensure percentage is between 0 and 100
      if (percentage >= 0 && percentage <= 100) {
        // Store as regular percentage, TokenForm will convert to basis points
        setValue("maxWalletPercentage", percentage.toString());
        const tokenAmount = calculateTokenAmount(percentage);
        setValue("maxWalletAmount", tokenAmount);
      }
    } else {
      setValue("maxWalletPercentage", "0");
      setValue("maxWalletAmount", "0");
    }
  };

  const handleMaxBuyChange = (value: string) => {
    setMaxBuyType(value);
    
    if (value === NO_LIMIT_DISPLAY) {
      setValue("antiDump.maxBuyPercentage", NO_LIMIT_VALUE);
      setValue("maxBuyAmount", watch("totalSupply") || "0");
      setValue("antiDump.enabled", false);
    } else if (value === "custom") {
      setValue("antiDump.maxBuyPercentage", "0");
      setValue("maxBuyAmount", "0");
      setValue("antiDump.enabled", true);
    } else {
      // Store as regular percentage, TokenForm will convert to basis points
      setValue("antiDump.maxBuyPercentage", value);
      setValue("antiDump.enabled", true);
      
      // Convert percentage to actual token amount for display
      const tokenAmount = calculateTokenAmount(parseInt(value));
      setValue("maxBuyAmount", tokenAmount);
    }
  };

  const handleCustomMaxBuyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomMaxBuy(value);
    
    if (value && !isNaN(parseFloat(value))) {
      const percentage = parseFloat(value);
      // Ensure percentage is between 0 and 100
      if (percentage >= 0 && percentage <= 100) {
        // Store as regular percentage, TokenForm will convert to basis points
        setValue("antiDump.maxBuyPercentage", percentage.toString());
        setValue("antiDump.enabled", true);
        const tokenAmount = calculateTokenAmount(percentage);
        setValue("maxBuyAmount", tokenAmount);
      }
    } else {
      setValue("antiDump.maxBuyPercentage", "0");
      setValue("maxBuyAmount", "0");
    }
  };

  const handleMaxSellChange = (value: string) => {
    setMaxSellType(value);
    
    if (value === NO_LIMIT_DISPLAY) {
      setValue("antiDump.maxSellPercentage", NO_LIMIT_VALUE);
      setValue("maxSellAmount", watch("totalSupply") || "0");
      setValue("antiDump.enabled", false);
    } else if (value === "custom") {
      setValue("antiDump.maxSellPercentage", "0");
      setValue("maxSellAmount", "0");
      setValue("antiDump.enabled", true);
    } else {
      // Store as regular percentage, TokenForm will convert to basis points
      setValue("antiDump.maxSellPercentage", value);
      setValue("antiDump.enabled", true);
      
      // Convert percentage to actual token amount for display
      const tokenAmount = calculateTokenAmount(parseInt(value));
      setValue("maxSellAmount", tokenAmount);
    }
  };

  const handleCustomMaxSellChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomMaxSell(value);
    
    if (value && !isNaN(parseFloat(value))) {
      const percentage = parseFloat(value);
      // Ensure percentage is between 0 and 100
      if (percentage >= 0 && percentage <= 100) {
        // Store as regular percentage, TokenForm will convert to basis points
        setValue("antiDump.maxSellPercentage", percentage.toString());
        setValue("antiDump.enabled", true);
        const tokenAmount = calculateTokenAmount(percentage);
        setValue("maxSellAmount", tokenAmount);
      }
    } else {
      setValue("antiDump.maxSellPercentage", "0");
      setValue("maxSellAmount", "0");
    }
  };

  const handleTradingDelayChange = (value: string) => {
    if (value === "custom") {
      setValue("tradingDelay", value);
      // Initialize custom value as empty
      setValue("customTradingDelay", "");
    } else {
      // For preset values, store the seconds directly
      setValue("tradingDelay", value);
    }
  };

  const handleCustomTradingDelayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const minutes = e.target.value;
    setValue("customTradingDelay", minutes);
    // Convert minutes to seconds for the contract
    if (minutes) {
      setValue("tradingDelayInSeconds", Math.floor(parseFloat(minutes) * 60).toString());
    }
  };

  const handleContractLockChange = (checked: boolean) => {
    if (checked) {
      // Show warning before enabling
      if (window.confirm("WARNING: This action is IRREVERSIBLE. Once the contract is locked:\n\n• No one will have administrative access\n• Contract settings cannot be modified\n• Ownership cannot be reclaimed\n\nAre you sure you want to proceed?")) {
        setValue("contractLocked", true);
      }
    } else {
      setValue("contractLocked", false);
    }
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground">
      <div className="flex items-center justify-between p-6">
        <div>
          <h3 className="text-lg font-semibold">Security Features</h3>
          <p className="text-sm text-muted-foreground">Configure your token's security settings</p>
        </div>
        <span
          className={`transform transition-transform duration-200 cursor-pointer ${isOpen ? 'rotate-180' : ''}`}
          style={{
            color: `var(--color-secondary, #10b981)`
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          ▼
        </span>
      </div>
      <div className={`transition-all duration-200 overflow-hidden ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-6 pt-0 space-y-6">
          {/* Basic Security */}
          <div>
            <FormField
              label={
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span>Max Wallet</span>
                    <span className="text-xs bg-blue-500/10 text-blue-500 px-2 py-0.5 rounded flex items-center gap-1">
                      <span>🐋</span>
                      <span>Anti-Whale</span>
                    </span>
                  </div>
                  {renderLockToggle(maxWalletLocked, setMaxWalletLocked)}
                </div>
              }
              tooltip="Maximum tokens per wallet (% of total supply). If set, max buy/sell cannot exceed this limit."
              input={
                <div className="flex flex-col space-y-2">
                  <Select
                    value={maxWalletType}
                    onValueChange={handleMaxWalletChange}
                    disabled={maxWalletLocked}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select max wallet limit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_LIMIT_DISPLAY}>No Limit</SelectItem>
                      <SelectItem value="1">1% (Ultra Strict)</SelectItem>
                      <SelectItem value="2">2% (Standard)</SelectItem>
                      <SelectItem value="5">5% (Moderate)</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {maxWalletType === "custom" && (
                    <div className="flex items-center space-x-2">
                      <Input
                        type="number"
                        placeholder="Enter percentage (0-100)"
                        value={customMaxWallet}
                        onChange={handleCustomMaxWalletChange}
                        min="0"
                        max="100"
                        step="0.1"
                        disabled={maxWalletLocked}
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  )}
                </div>
              }
            />
          </div>

          {/* Anti-Dump Protection */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormField
                  label={
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span>Max Buy Amount</span>
                        <span className="text-xs bg-green-500/10 text-green-500 px-2 py-0.5 rounded flex items-center gap-1">
                          <span>📈</span>
                          <span>Anti-Pump</span>
                        </span>
                      </div>
                      {renderLockToggle(maxBuyLocked, setMaxBuyLocked)}
                    </div>
                  }
                  tooltip="Maximum amount per buy (% of total supply)"
                  input={
                    <div className="flex flex-col space-y-2">
                      <Select
                        value={maxBuyType}
                        onValueChange={handleMaxBuyChange}
                        disabled={maxBuyLocked}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select max buy limit" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_LIMIT_DISPLAY}>No Limit</SelectItem>
                          <SelectItem value="1">1% (Ultra Strict)</SelectItem>
                          <SelectItem value="2">2% (Standard)</SelectItem>
                          <SelectItem value="5">5% (Moderate)</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {maxBuyType === "custom" && (
                        <div className="flex items-center space-x-2">
                          <Input
                            type="number"
                            placeholder="Enter percentage (0-100)"
                            value={customMaxBuy}
                            onChange={handleCustomMaxBuyChange}
                            min="0"
                            max="100"
                            step="0.1"
                            disabled={maxBuyLocked}
                          />
                          <span className="text-sm text-muted-foreground">%</span>
                        </div>
                      )}
                    </div>
                  }
                />
              </div>

              <div>
                <FormField
                  label={
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span>Max Sell Amount</span>
                        <span className="text-xs bg-red-500/10 text-red-500 px-2 py-0.5 rounded flex items-center gap-1">
                          <span>📉</span>
                          <span>Anti-Dump</span>
                        </span>
                      </div>
                      {renderLockToggle(maxSellLocked, setMaxSellLocked)}
                    </div>
                  }
                  tooltip="Maximum amount per sell (% of total supply)"
                  input={
                    <div className="flex flex-col space-y-2">
                      <Select
                        value={maxSellType}
                        onValueChange={handleMaxSellChange}
                        disabled={maxSellLocked}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select max sell limit" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_LIMIT_DISPLAY}>No Limit</SelectItem>
                          <SelectItem value="1">1% (Ultra Strict)</SelectItem>
                          <SelectItem value="2">2% (Standard)</SelectItem>
                          <SelectItem value="5">5% (Moderate)</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {maxSellType === "custom" && (
                        <div className="flex items-center space-x-2">
                          <Input
                            type="number"
                            placeholder="Enter percentage (0-100)"
                            value={customMaxSell}
                            onChange={handleCustomMaxSellChange}
                            min="0"
                            max="100"
                            step="0.1"
                            disabled={maxSellLocked}
                          />
                          <span className="text-sm text-muted-foreground">%</span>
                        </div>
                      )}
                    </div>
                  }
                />
              </div>
            </div>

            <div>
              <FormField
                label={
                  <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span>Trading Delay</span>
                    <span className="text-xs bg-yellow-500/10 text-yellow-500 px-2 py-0.5 rounded flex items-center gap-1">
                      <span>🤖</span>
                      <span>Anti-Sniper</span>
                    </span>
                  </div>
                  {renderLockToggle(tradingDelayLocked, setTradingDelayLocked)}
                </div>
                }
                tooltip="Delay before trading can start (in minutes). Helps prevent sniping bots at launch."
                input={
                  <Select 
                    value={watch("tradingDelay") || "0"} 
                    onValueChange={handleTradingDelayChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select trading delay" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">No Delay</SelectItem>
                      <SelectItem value="0.5">0.5 minutes</SelectItem>
                      <SelectItem value="1">1 minute</SelectItem>
                      <SelectItem value="5">5 minutes</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                }
              />
              {watch("tradingDelay") === "custom" && (
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    type="number"
                    placeholder="Enter delay in minutes"
                    className="mt-2"
                    min="0"
                    step="0.5"
                    value={watch("customTradingDelay") || ""}
                    onChange={handleCustomTradingDelayChange}
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">minutes</span>
                </div>
              )}
            </div>
          </div>

          {/* Trading Protection */}
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span>Trading Status</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="w-4 h-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Configure how trading will be enabled for your token</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="permanentTrading"
                  checked={watch("permanentTrading")}
                  onCheckedChange={(checked) => {
                    setValue("permanentTrading", checked);
                  }}
                />
                <label
                  htmlFor="permanentTrading"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Enable Permanent Trading on Deploy (Cannot be Disabled)
                </label>
              </div>
              <div className="text-sm text-muted-foreground">
                When enabled, trading will start immediately after deployment and cannot be paused. If disabled, you can enable/disable trading later through TokenTools.
              </div>
            </div>

            <div className="space-y-2 mt-4">
              <div className="flex items-center gap-2">
                <span>Initial Trading Status</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="w-4 h-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Enable trading immediately after token deployment</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="tradingEnabled"
                    checked={watch("tradingEnabled") || watch("permanentTrading")}
                    onCheckedChange={(checked) => {
                      setValue("tradingEnabled", checked);
                    }}
                    disabled={watch("permanentTrading")}
                  />
                  <label
                    htmlFor="tradingEnabled"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Enable Trading on Deploy
                  </label>
                </div>
                {renderLockToggle(tradingEnabledLocked, (checked) => {
                  setTradingEnabledLocked(checked);
                  setValue("tradingEnabledLocked", checked);
                })}
              </div>
              <div className="text-sm text-muted-foreground">
                {watch("permanentTrading") 
                  ? "Trading will be permanently enabled since 'Enable Trading on Deploy (Cannot be Disabled)' is checked"
                  : "If enabled, trading will be active immediately after deployment"}
              </div>
            </div>
          </div>

          {/* Contract Lock - Last Section */}
          {isTestnet && (
            <div className="space-y-4 border-t pt-4 mt-6">
              <div className="flex items-start space-x-2">
                <FormField
                  label={
                    <div className="flex items-center space-x-2">
                      <span className="text-red-500 font-semibold">Contract Lock</span>
                    </div>
                  }
                  tooltip="Enabling this will permanently lock the contract by: Revoking all administrative privileges, Renouncing contract ownership, Preventing any future modifications to contract settings"
                  input={
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="contractLock"
                          checked={watch("contractLocked")}
                          onCheckedChange={handleContractLockChange}
                        />
                        <label
                          htmlFor="contractLock"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Permanently lock contract (Renounce ownership)
                        </label>
                      </div>

                      {watch("contractLocked") && (
                        <div className="pl-6 space-y-4">
                          <div className="bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                            <p className="text-sm text-red-600 dark:text-red-400">
                              ⚠️ This action is IRREVERSIBLE. Once the contract is locked:
                              <br />• No one will have administrative access
                              <br />• Contract settings cannot be modified
                              <br />• Ownership cannot be reclaimed
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  }
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}